import {FormCommonProps} from "../../../../common/forms/types.ts";
import {
	CustomerOrderLineReservationDto
} from "../../../../../api/generated/erp/sales/picking/customerOrderLineReservationDto.ts";
import {Control, useFieldArray, useForm, useWatch} from "react-hook-form";
import {
	CustomerOrderLineReservationFormApi,
	CustomerOrderLineReservationFormApi_InitData,
	CustomerOrderLineReservationFormApi_PartWarehouseLocationDto,
} from "../../../../../api/generated/erp/sales/picking/api/customerOrderLineReservationFormApi.ts";
import {AavoForm} from "../../../../common/forms/AavoForm.tsx";
import {FormTextField} from "../../../../common/forms/fields/FormTextField.tsx";
import i18n from "i18next";
import {nonNegativeFloatRule} from "../../../../common/forms/validation.ts";
import {Box, Typography} from "@mui/material";
import {AsyncFetchRender} from "../../../../common/async/AsyncFetchRender.tsx";
import {filterNulls} from "../../../../../utils/arrays.ts";
import {FormSelectField} from "../../../../common/forms/fields/FormSelectField.tsx";
import {formatIsoString} from "src/utils/dayjsUtils.ts";
import {Theme} from "@mui/material/styles";

export interface CustomerOrderLineReservationFormProps extends FormCommonProps<void> {
	customerOrderLines: CustomerOrderLineReservationDto[];
	onlyFromOwnWarehouseArea: boolean;
}

export const CustomerOrderLineReservationForm = (props: CustomerOrderLineReservationFormProps) => {
	const { customerOrderLines, onlyFromOwnWarehouseArea } = props;
	return (
		<AsyncFetchRender
			fetch={() =>
				CustomerOrderLineReservationFormApi.fetchInitData({
					partIds: filterNulls(customerOrderLines.map((line) => line.partId)),
					onlyFromOwnWarehouseArea: onlyFromOwnWarehouseArea,
				})
			}
			content={(initData) => (
				<CustomerOrderLineReservationFormContent initData={initData} {...props} />
			)}
		/>
	);
};

interface CustomerOrderLineReservationFormContentProps
	extends CustomerOrderLineReservationFormProps {
	initData: CustomerOrderLineReservationFormApi_InitData;
}

interface FormValues {
	reservationRequests: ReservationRequestFormValues[];
}

interface ReservationRequestFormValues {
	customerOrderLine: CustomerOrderLineReservationDto;
	partWarehouseLocationOptions: CustomerOrderLineReservationFormApi_PartWarehouseLocationDto[];
	partWarehouseLocationId: number | null;
	quantityToReserveInWarehouseUnit: number;
}

const CustomerOrderLineReservationFormContent = ({
	initData,
	onCompleted,
	customerOrderLines,
}: CustomerOrderLineReservationFormContentProps) => {
	const {
		control,
		handleSubmit,
		formState: { isDirty },
	} = useForm<FormValues>({
		mode: "all",
		defaultValues: {
			reservationRequests: customerOrderLines.map((line) => {
				const partWarehouseLocationOptions =
					line.partId == null
						? []
						: initData.partWarehouseLocationOptionsByPartIds[line.partId] ?? [];
				return {
					customerOrderLine: line,
					partWarehouseLocationOptions: partWarehouseLocationOptions,
					partWarehouseLocationId:
						partWarehouseLocationOptions[0]?.partWarehouseLocationId,
					quantityToReserveInWarehouseUnit:
						line.salesQuantityAsWarehouseUnits - line.reservedQuantity,
				};
			}),
		},
	});

	const requestsFieldArray = useFieldArray({
		control,
		name: "reservationRequests",
	});

	const watchReservationRequests = useWatch({ control, name: "reservationRequests" });

	return (
		<AavoForm
			handleSubmit={handleSubmit}
			submit={submitForm}
			isDirty={isDirty}
			onCompleted={onCompleted}
		>
			{requestsFieldArray.fields.map((field, index) => (
				<CustomerOrderLineFormSection
					key={field.id}
					currentValues={watchReservationRequests[index]!}
					control={control}
					index={index}
				/>
			))}
		</AavoForm>
	);
};

interface CustomerOrderLineFormSectionProps {
	currentValues: ReservationRequestFormValues;
	control: Control<FormValues> | undefined;
	index: number;
}

const CustomerOrderLineFormSection = ({
	currentValues,
	control,
	index,
}: CustomerOrderLineFormSectionProps) => {
	const currentWarehouseLocation = currentValues.partWarehouseLocationOptions.find(
		(option) => option.partWarehouseLocationId == currentValues?.partWarehouseLocationId,
	);

	const customerOrderLine = currentValues.customerOrderLine;
	const availableQuantity = calculateAvailableQuantity(currentWarehouseLocation);
	const quantityToReserve =
		customerOrderLine.salesQuantityAsWarehouseUnits - customerOrderLine.reservedQuantity;

	return (
		<Box
			sx={{
				borderBottom: "1px solid",
				borderColor: (theme: Theme) => theme.palette.divider,
				display: "flex",
				gap: 1,
				paddingBottom: 1,
				marginBottom: 1,
			}}
		>
			<Box
				sx={{
					flex: 1,
				}}
			>
				<Typography
					variant={"h6"}
				>{`${customerOrderLine.partNo} | ${customerOrderLine.partDescription_1} | ${customerOrderLine.partDescription_2}`}</Typography>
				<Typography>
					{i18n.t("delivery_date")}:{" "}
					{formatIsoString(customerOrderLine.plannedDeliveryDate)}
				</Typography>
			</Box>
			<Box
				sx={{
					minWidth: 200,
					display: "flex",
					gap: 1,
					flexDirection: "column",
				}}
			>
				<FormSelectField
					control={control}
					name={`reservationRequests.${index}.partWarehouseLocationId`}
					label={i18n.t("warehouse_location")}
					options={currentValues.partWarehouseLocationOptions.map((option) => ({
						key: option.partWarehouseLocationId,
						label: `${option.partWarehouseLocationCode} ${
							calculateAvailableQuantity(option) == null
								? ""
								: `(${calculateAvailableQuantity(option)} ${option.partWarehouseUnit})`
						}`,
					}))}
				/>

				<Typography
					sx={{
						color:
							availableQuantity !== null && availableQuantity < quantityToReserve
								? "error.main"
								: "unset",
					}}
				>
					{getInventoryLevelStatus(currentWarehouseLocation)}
				</Typography>
			</Box>
			<Box
				sx={{
					display: "flex",
					gap: 1,
					flexDirection: "column",
				}}
			>
				<FormTextField
					control={control}
					name={`reservationRequests.${index}.quantityToReserveInWarehouseUnit`}
					label={i18n.t("quantity")}
					rules={nonNegativeFloatRule()}
				/>
				<Typography>
					{i18n.t("quantity_to_reserve")}: {quantityToReserve}
				</Typography>
			</Box>
		</Box>
	);
};

const getInventoryLevelStatus = (
	location: CustomerOrderLineReservationFormApi_PartWarehouseLocationDto | undefined,
) => {
	if (location?.inventoryLevelEnabled === false) return i18n.t("inventory_level_disabled");

	const availableQuantity = calculateAvailableQuantity(location);
	if (availableQuantity == null) return "-";
	return `${i18n.t("available")}: ${availableQuantity}`;
};

const calculateAvailableQuantity = (
	location: CustomerOrderLineReservationFormApi_PartWarehouseLocationDto | undefined | null,
) => {
	if (location?.stockQuantity == null || location?.reservedQuantity == null) return null;
	return location.stockQuantity - location.reservedQuantity;
};

const submitForm = ({ reservationRequests }: FormValues) =>
	CustomerOrderLineReservationFormApi.createReservations({
		reservationRequests: reservationRequests.map((request) => ({
			customerOrderLineId: request.customerOrderLine.customerOrderLineId,
			partWarehouseLocationId: request.partWarehouseLocationId,
			quantityInWarehouseUnit: request.quantityToReserveInWarehouseUnit,
		})),
	});
